import { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  FileText,
  Gavel,
  LoaderCircle,
  Search,
} from "lucide-react";
import {
  DataTable,
  DataTableAction,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTablePrimaryCell,
  DataTableRow,
  DataTableStatus,
  DataTableValue,
  PageHeader,
  CopyableText,
  CopyableDate,
  useRouter,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import {
  buildSentenceSearchUrl,
  prepareSentenceSearchQuery,
  sentencePaginationItems,
  sentenceSearchPageFromUrl,
  sentenceSearchQueryFromUrl,
} from "../lib/sentence-search-query.js";

function formatDate(value) {
  if (!value) return "Fecha no informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha no informada";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(
    date,
  );
}

function sentenceKey(cuij, actId) {
  return `${cuij}:${actId}`;
}

export function SentenceSearchPage() {
  const api = useApi();
  const { search: routeSearch, navigate } = useRouter();
  const routeQuery = sentenceSearchQueryFromUrl(routeSearch);
  const routePage = sentenceSearchPageFromUrl(routeSearch);
  const [query, setQuery] = useState(routeQuery);
  const [reloadToken, setReloadToken] = useState(0);
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [expandedCuij, setExpandedCuij] = useState("");
  const [sentencesByCuij, setSentencesByCuij] = useState({});
  const [loadingCuij, setLoadingCuij] = useState("");
  const [rowErrors, setRowErrors] = useState({});
  const [pendingDocument, setPendingDocument] = useState("");
  const [attachmentsBySentence, setAttachmentsBySentence] = useState({});

  useEffect(() => {
    let active = true;
    const prepared = prepareSentenceSearchQuery(routeQuery);
    setQuery(routeQuery);
    setError("");
    setExpandedCuij("");
    setRowErrors({});
    setAttachmentsBySentence({});

    if (!routeQuery) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      setPagination({ page: 1, pageSize: 10, totalItems: 0, totalPages: 1 });
      return () => {
        active = false;
      };
    }
    if (!prepared.valid) {
      setResults([]);
      setSearched(true);
      setLoading(false);
      setError(
        "Ingresá un número de expediente, un CUIJ o al menos 3 caracteres de la carátula.",
      );
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setSearched(false);
    api.searchJudiciarySentences({
      query: prepared.query,
      page: routePage,
      limit: 10,
    }).then((pageData) => {
      if (!active) return;
      setResults(pageData.items || []);
      setPagination({
        page: pageData.page,
        pageSize: pageData.pageSize,
        totalItems: pageData.totalItems,
        totalPages: Math.max(1, pageData.totalPages),
      });
      setSearched(true);
    }).catch((requestError) => {
      if (!active) return;
      setResults([]);
      setSearched(true);
      setError(requestError.message);
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [api, reloadToken, routePage, routeQuery]);

  const submitSearch = (event) => {
    event.preventDefault();
    const prepared = prepareSentenceSearchQuery(query);
    if (!prepared.valid) {
      setError(
        "Ingresá un número de expediente, un CUIJ o al menos 3 caracteres de la carátula.",
      );
      return;
    }
    if (prepared.query === routeQuery && routePage === 1) {
      setReloadToken((current) => current + 1);
      return;
    }
    navigate(buildSentenceSearchUrl(prepared.query));
  };

  const toggleSentences = async (result) => {
    if (expandedCuij === result.cuij) {
      setExpandedCuij("");
      return;
    }
    setExpandedCuij(result.cuij);
    if (sentencesByCuij[result.cuij] || loadingCuij) return;
    setLoadingCuij(result.cuij);
    setRowErrors((current) => ({ ...current, [result.cuij]: "" }));
    try {
      const data = await api.listJudiciarySentences(result.cuij);
      setSentencesByCuij((current) => ({
        ...current,
        [result.cuij]: data.items || [],
      }));
    } catch (requestError) {
      setRowErrors((current) => ({
        ...current,
        [result.cuij]: requestError.message,
      }));
    } finally {
      setLoadingCuij("");
    }
  };

  const openSentence = async (result, sentence, attachmentId = null) => {
    const key = sentenceKey(result.cuij, sentence.actId);
    if (pendingDocument) return;
    setPendingDocument(key);
    setRowErrors((current) => ({ ...current, [result.cuij]: "" }));
    try {
      const response = await api.openJudiciarySentence({
        cuij: result.cuij,
        actId: sentence.actId,
        attachmentId,
      });
      if (response?.status === "select_attachment") {
        setAttachmentsBySentence((current) => ({
          ...current,
          [key]: response.attachments || [],
        }));
      } else if (response?.status === "unavailable") {
        setRowErrors((current) => ({
          ...current,
          [result.cuij]: "PJM no tiene disponible el PDF de esta sentencia.",
        }));
      } else {
        setAttachmentsBySentence((current) => ({ ...current, [key]: [] }));
      }
    } catch (requestError) {
      setRowErrors((current) => ({
        ...current,
        [result.cuij]: requestError.message,
      }));
    } finally {
      setPendingDocument("");
    }
  };

  const goToPage = (page) => {
    if (page === pagination.page || page < 1 || page > pagination.totalPages) {
      return;
    }
    navigate(buildSentenceSearchUrl(routeQuery, { page }));
  };
  const paginationItems = sentencePaginationItems(
    pagination.page,
    pagination.totalPages,
  );

  return (
    <div className="page expediente-search-page instrumenta-directory-page sentence-search-page">
      <PageHeader
        hero
        eyebrow="Instrumenta · Poder Judicial de Mendoza"
        title="Buscar sentencias"
        description="Consultá por número de expediente, CUIJ o texto de la carátula. Los resultados se limitan a causas que PJM identifica con sentencia."
      />

      <section className="expediente-lookup-card">
        <div className="expediente-lookup-copy">
          <span className="expediente-lookup-icon">
            <Search size={21} />
          </span>
          <strong>Consultar sentencias</strong>
        </div>
        <form className="expediente-lookup-form" onSubmit={submitSearch}>
          <label htmlFor="sentence-query">
            <input
              id="sentence-query"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setError("");
              }}
              placeholder="Número de expediente, CUIJ o texto de la carátula"
              aria-label="Número de expediente, CUIJ o texto de la carátula"
              autoComplete="off"
              autoFocus
              disabled={loading}
              aria-invalid={Boolean(error)}
            />
          </label>
          <button className="button primary" type="submit" disabled={loading}>
            {loading ? (
              <LoaderCircle className="spin" size={18} />
            ) : (
              <Search size={18} />
            )}
            {loading ? "Consultando…" : "Consultar"}
          </button>
        </form>

        {error && (
          <p className="field-error expediente-lookup-error">{error}</p>
        )}
      </section>

      <section className="sentence-results" aria-live="polite">
        {results.length > 0 && (
          <div className="tax-results-heading">
            <div>
              <span className="eyebrow">Resultados con sentencia</span>
              <h2>
                {pagination.totalItems} expediente
                {pagination.totalItems === 1 ? "" : "s"}
              </h2>
            </div>
            <Gavel size={20} />
          </div>
        )}

        {results.length > 0 && (
          <DataTable
            ariaLabel="Expedientes con sentencia"
            className="sentence-results-table"
            density="compact"
            minWidth="820px"
          >
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell className="sentence-table-case">
                  Expediente
                </DataTableHeaderCell>
                <DataTableHeaderCell priority="secondary">
                  Organismo
                </DataTableHeaderCell>
                <DataTableHeaderCell className="sentence-table-status">
                  Estado
                </DataTableHeaderCell>
                <DataTableHeaderCell align="end" className="sentence-table-action">
                  Documento
                </DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {results.map((result) => {
                const expanded = expandedCuij === result.cuij;
                const sentences = sentencesByCuij[result.cuij];
                const rowLoading = loadingCuij === result.cuij;
                return [
                  <DataTableRow
                    key={result.id || result.cuij}
                    label={`Ver sentencias de ${result.cuij}`}
                    onActivate={() => toggleSentences(result)}
                  >
                    <DataTableCell>
                      <DataTablePrimaryCell
                        icon={<Gavel size={16} />}
                        eyebrow={(
                          <CopyableText
                            className="sentence-result-copy"
                            value={result.cuij}
                            label="CUIJ"
                          >
                            {result.cuij}
                          </CopyableText>
                        )}
                        title={result.caratula || "Sin carátula informada"}
                        meta={result.numero ? (
                          <CopyableText
                            className="sentence-result-copy"
                            value={result.numero}
                            label="número de expediente"
                          >
                            Expediente N.º {result.numero}
                          </CopyableText>
                        ) : null}
                      />
                    </DataTableCell>
                    <DataTableCell priority="secondary">
                      <DataTableValue
                        primary={result.organismoActual || "No informado"}
                        secondary={result.fuero || "Fuero no informado"}
                      />
                    </DataTableCell>
                    <DataTableCell>
                      <DataTableStatus
                        label="Con sentencia"
                        description="Informado por PJM"
                        tone="success"
                      />
                    </DataTableCell>
                    <DataTableCell align="end">
                      <DataTableAction
                        label={`${expanded ? "Ocultar" : "Ver"} sentencias de ${result.cuij}`}
                        onClick={() => toggleSentences(result)}
                        icon={rowLoading
                          ? <LoaderCircle className="spin" size={15} />
                          : expanded
                            ? <ChevronUp size={15} />
                            : <ChevronDown size={15} />}
                      >
                        {rowLoading ? "Cargando…" : "Ver sentencias"}
                      </DataTableAction>
                    </DataTableCell>
                  </DataTableRow>,
                  expanded && (
                    <DataTableRow
                      className="sentence-detail-row"
                      key={`${result.id || result.cuij}:sentences`}
                    >
                      <DataTableCell colSpan={4}>
                        <div className="sentence-detail-panel">
                          {rowLoading && (
                            <div className="sentence-detail-message">
                              <LoaderCircle className="spin" size={18} />
                              Consultando las actuaciones en PJM…
                            </div>
                          )}
                          {rowErrors[result.cuij] && (
                            <p className="sentence-row-error">
                              {rowErrors[result.cuij]}
                            </p>
                          )}
                          {!rowLoading && sentences?.length === 0 && (
                            <div className="sentence-detail-message">
                              PJM marca el expediente como resuelto, pero no
                              expone una actuación de sentencia identificable.
                            </div>
                          )}
                          {sentences?.map((sentence) => {
                            const key = sentenceKey(result.cuij, sentence.actId);
                            const attachments = attachmentsBySentence[key] || [];
                            return (
                              <div className="sentence-document-row" key={sentence.actId}>
                                <span className="sentence-document-icon">
                                  <FileText size={17} />
                                </span>
                                <span className="sentence-document-copy">
                                  <strong>{sentence.title}</strong>
                                  <small>
                                    <CopyableDate value={sentence.date}>
                                      {formatDate(sentence.date)}
                                    </CopyableDate>{" "}
                                    · Identificada
                                    por metadatos de PJM
                                  </small>
                                  {attachments.length > 0 && (
                                    <span className="sentence-attachments">
                                      {attachments.map((attachment) => (
                                        <button
                                          type="button"
                                          key={attachment.id}
                                          disabled={pendingDocument === key}
                                          onClick={() => openSentence(
                                            result,
                                            sentence,
                                            attachment.id,
                                          )}
                                        >
                                          {attachment.title}
                                        </button>
                                      ))}
                                    </span>
                                  )}
                                </span>
                                <button
                                  type="button"
                                  className="sentence-open-button"
                                  disabled={Boolean(pendingDocument)}
                                  onClick={() => openSentence(result, sentence)}
                                >
                                  {pendingDocument === key
                                    ? <LoaderCircle className="spin" size={15} />
                                    : <ExternalLink size={15} />}
                                  Abrir PDF
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  ),
                ];
              })}
            </DataTableBody>
          </DataTable>
        )}

        {results.length > 0 && pagination.totalPages > 1 && (
          <nav className="sentence-pagination" aria-label="Páginas de resultados">
            <button
              type="button"
              aria-label="Página anterior"
              disabled={pagination.page <= 1 || loading}
              onClick={() => goToPage(pagination.page - 1)}
            >
              <ChevronLeft size={17} />
            </button>
            {paginationItems.map((item) => typeof item === "number" ? (
              <button
                className={item === pagination.page ? "active" : ""}
                type="button"
                key={item}
                aria-label={`Página ${item}`}
                aria-current={item === pagination.page ? "page" : undefined}
                disabled={loading}
                onClick={() => goToPage(item)}
              >
                {item}
              </button>
            ) : (
              <span key={item} aria-hidden="true">…</span>
            ))}
            <button
              type="button"
              aria-label="Página siguiente"
              disabled={pagination.page >= pagination.totalPages || loading}
              onClick={() => goToPage(pagination.page + 1)}
            >
              <ChevronRight size={17} />
            </button>
          </nav>
        )}

        {searched && !loading && !error && results.length === 0 && (
          <div className="tax-empty-state sentence-empty-state">
            <Search size={25} />
            <h2>No encontramos sentencias</h2>
            <p>Probá con menos palabras o revisá el número ingresado.</p>
          </div>
        )}
      </section>
    </div>
  );
}
